<template>
  <div class="mb-5">
    <div class="ps-3 pt-3">
      <h2 class="mb-2 font-weight-bold">{{ $t('setting.title') }} {{ $t('invoice.title') }} ({{ billButtonType == 'monthly' ? $t('monthly') : $t('daily') }})</h2>
    </div>
    <div class="d-flex align-center justify-space-between pb-3">
      <div class="ps-3">
        <v-btn-toggle :value="billingSettingType" color="info" mandatory>
          <v-btn value="monthly" @click="changeBillingSettingType('monthly'), billButtonType = 'monthly'">{{ $t('monthly') }}</v-btn>
          <v-btn value="daily" @click="changeBillingSettingType('daily'), billButtonType = 'daily'">{{ $t('daily') }}</v-btn>
        </v-btn-toggle>
      </div>
      <div class="d-flex align-center justify-end gap-2">
        <v-btn v-if="!isShowSelect" color="info" outlined class="mx-1" :disabled="isMainLoading"
          @click="seleteArea('setting')">
          <v-icon class="me-2">mdi-cog</v-icon>{{ $t('market.setting_multiple_stall') }}
        </v-btn>
        <v-btn v-if="!isShowSelect" color="info" outlined class="mx-1" :disabled="isMainLoading"
          @click="seleteArea('addCost')">
          <v-icon class="me-2">mdi-cash-multiple</v-icon>{{ $t('market.adjust_multiple_stall') }}
        </v-btn>
        <template v-if="isShowSelect">
          <v-btn v-if="selectType == 'setting'" color="info" outlined class="mx-1"
            :disabled="isMainLoading || seleteAreas.length == 0" @click="addBillingSettings()">
            <v-icon class="me-2">mdi-plus</v-icon>
            {{ $t('market.setting_all_stall', { count: seleteAreas.length }) }}
          </v-btn>
          <v-btn v-if="selectType == 'setting'" color="error" outlined class="mx-1"
            :disabled="isMainLoading || seleteAreas.length == 0" @click="removeBillingSettings()">
            <v-icon class="me-2">mdi-trash-can</v-icon>
            {{ $t('delete_settings') }} {{ seleteAreas.length }} {{ $t('area.title') }}
          </v-btn>
          <v-btn v-else-if="selectType == 'addCost'" color="info" outlined class="mx-1"
            :disabled="isMainLoading || seleteAreas.length == 0" @click="addCosts()">
            <v-icon class="me-2">mdi-cash-multiple</v-icon>
            {{ $t('market.adjust_all_price_stall', { price: seleteAreas.length }) }}
          </v-btn>
          <v-btn class="error mx-1" :disabled="isMainLoading"
            @click="seleteAreas = []; seleteMeters = []; isShowSelect = false; selectType = ''">{{ $t('cancel') }}</v-btn>
        </template>
      </div>
    </div>

    <v-card flat outlined :loading="isMainLoading" min-height="30">
      <v-data-table v-if="billingTable.headers" :loading="isBillingLoading || isMainLoading" v-model="seleteAreas"
        :items-per-page="10" :headers="billingTable.headers" :items="areaBillingSettingItems" class="elevation-0 outlined"
        sort-by="order" item-key="objectId" :show-select="isShowSelect">
        <template v-slot:body.prepend>
          <tr>
            <td class="text-center" :colspan="isShowSelect ? 2 : 1">
              <v-icon>mdi-filter-multiple-outline</v-icon>
            </td>
            <td class="ps-0">
              <v-text-field v-model="filterData.areaName" type="text" :label="$t('area.area_name')" solo dense hide-details />
            </td>
            <td class="ps-0">
              <v-text-field v-model="filterData.serviceName" type="text" :label="$t('service')" solo dense hide-details />
            </td>
            <td class="ps-0">
              <v-autocomplete v-model="filterData.unit" :items="[{ text: $t('all'), value: '' }, ...filterOption.unit]"
                :label="$t('area.method_calculate')" item-text="text" solo dense hide-details clearable
                :no-data-text="$t('area.no_method_calculate')"></v-autocomplete>
            </td>
            <td class="ps-0">
              <v-text-field v-model="filterData.price" class="input-right input-disable-appearance" type="text"
                :label="$t('price')" solo dense hide-details />
            </td>
            <td class="ps-0">
              <v-text-field v-model="filterData.discount" class="input-right input-disable-appearance" type="text"
                :label="$t('discount')" solo dense hide-details />
            </td>
            <td class="ps-0 pe-0">
              <v-text-field v-model="filterData.total" class="input-right input-disable-appearance" type="text"
                :label="$t('total')" solo dense hide-details />
            </td>
            <td class="ps-0"></td>
            <td class="ps-0"></td>
          </tr>
        </template>
        <template v-slot:item.order="{ index }">{{ index + 1 }}</template>
        <template v-slot:item.serviceText="{ item }">
          <div v-for="(prod, index) in item.billingSettings" :key="`${item.objectId}_name_${index}`" class="py-1">
            <span class="nameProd">{{ prod.accountingProduct.name }}</span>
          </div>
          <span v-if="item.billingSettings.length < 1" class="px-3 warning rounded-xl white--text">{{ $t('not_setting') }}</span>
        </template>
        <template v-slot:item.costText="{ item }">
          <div v-for="(prod, index) in item.billingSettings" :key="`${item.objectId}_cost_${index}`" class="py-1">{{
            prod.cost | currency }}</div>
        </template>
        <template v-slot:item.discountText="{ item }">
          <div v-for="(prod, index) in item.billingSettings" :key="`${item.objectId}_discount_${index}`" class="py-1">{{
            (prod.discount || 0) | currency }}{{ prod.discountType == 2 ? '%' : '' }}</div>
        </template>
        <template v-slot:item.totalText="{ item }">
          <div v-for="(prod, index) in item.billingSettings" :key="`${item.objectId}_total_${index}`" class="py-1">
            <template v-if="prod.discountType == 2">{{ $helper.round(prod.cost - (prod.cost * (prod.discount || 0) / 100))
              | currency }}</template>
            <template v-else>{{ prod.cost - (prod.discount || 0) | currency }}</template>
          </div>
        </template>
        <template v-slot:item.unitText="{ item }">
          <div v-for="(prod, index) in item.billingSettings" :key="`${item.objectId}_unit_${index}`" class="py-1">{{
            prod.typeString }}</div>
        </template>
        <template v-slot:item.action="{ item }">
          <div v-for="(prod, index) in item.billingSettings" :key="`${item.objectId}_action_${index}`" class="py-0">
            <v-btn small color="yellow darken-2" icon @click="selectUpdate(prod, 'service', item)">
              <v-icon size="20">mdi-pencil</v-icon>
            </v-btn>
            <v-btn small color="error" icon @click="removeDialogId(prod.objectId, prod)">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.add="{ item }">
          <v-btn color="info" small rounded elevation="0" @click="addBillingSetting(item, 'service')">
            <v-icon left>mdi-plus</v-icon>{{ $t('add_setting') }}
          </v-btn>
        </template>
      </v-data-table>

      <form-billing-setting v-model="billingDialog" :area="tempArea" @onSave="onSaveBilling"
        @onSaveUpdate="saveUpdateBilling" :billingType="billingType" :event="event" :billingSetting="tempBillingSetting"
        :meter="tempMeter" :meterType="meterType" :isLoading="isBillingLoading"
        :billingSettingType="billingSettingType" :closeDialogConfirm="true" @onCloseDialog="dialogCloseDialogConfirm = true"
        :tabType="tabType" @onDelete="saveDeleteBilling"></form-billing-setting>
      <form-add-cost-billing-setting v-model="addCostBillingDialog" @onSave="saveAddCostList"
        :billingSettings="addCostBillingSettings" :isLoading="isBillingLoading" :tabType="tabType"></form-add-cost-billing-setting>
    </v-card>

    <confirm-dialog @onOk="removeBilling(removeId)" v-model="removeDialog" :disabled="isLoading"
      :loading="isLoading"></confirm-dialog>

    <LoadingPercentDialog v-model="isLoadingDialog" :final="loadingFinal" :initial="loadingInitial" />

    <v-dialog v-model="isGotoSendSettingTask" persistent max-width="500px">
      <v-card>
        <v-card-title class="primary--text">{{ $t('task.create_approve_setting_success') }}</v-card-title>
        <v-card-text class="py-5">
          <div style="text-indent:15px;">{{ $t('task.create_approve_setting_success_description_1') }}
          </div>
          <div style="text-indent:15px;">{{ $t('task.create_approve_setting_success_description_3') }}</div>
        </v-card-text>
        <v-card-actions class="mx-2">
          <v-spacer></v-spacer>
          <v-btn @click="isGotoSendSettingTask = false">
            <v-icon left>mdi-window-close</v-icon>{{ $t('close') }}
          </v-btn>
          <v-btn color="success" to="settingtask-billing" @click="isGotoSendSettingTask = false">
            <v-icon left>mdi-circle-outline</v-icon>{{ $t('task.goto_approve_setting_task') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialogComponent v-model="dialogCloseDialogConfirm" :title="$t('close_window')"
      :text="$t('validate.confirm_to_close_window')"
      :textClass="'text-center fs-14'"
      :textBtnOk="$t('confirm')" 
      :textBtnCancel="$t('cancel')"
      :type="'error'"
      @onOk="billingDialog = false; dialogCloseDialogConfirm = false"
      @onCancel="dialogCloseDialogConfirm = false" />

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ConfirmDialog from "./ConfirmDialog.vue";
import FormBillingSetting from "./FormBillingSetting.vue";
import FormAddCostBillingSetting from "./FormAddCostBillingSetting.vue";
import AreaAccountingProductManageDialog from "./AreaAccountingProductManageDialog.component.vue";
import LoadingPercentDialog from "@/components/LoadingPercentDialog.component";
import ConfirmDialogComponent from "./ConfirmDialog.component.vue";

export default {
  components: {     
    FormBillingSetting,
    FormAddCostBillingSetting,
    ConfirmDialog,
    AreaAccountingProductManageDialog,
    LoadingPercentDialog,
    ConfirmDialogComponent
  },
  props: {
    isMainLoading: Boolean,
    projectId: String,
    marketId: String,
    meterType: String,
    areaBillingSettingItems: Array,
    billingSettingType: String,
    tabType: String
  },
  computed: {
    admin() {
      return this.getAdminByMarketId(this.marketId);
    },
    employee() {
      return this.getEmployeeByMarketId(this.marketId);
    },
    ...mapGetters({
      getAdminByMarketId: "admin/getByMarketId",
      getEmployeeByMarketId: "employee/getByMarketId",
      billingSetting: "billingSetting/getBillingSettingsWithLabel",
      getAreas: "markets/getAreas",
      getSettingTasks: "settingTask/getSettingTasks"
    }),
    enableEditBillingSetting() {
      let res = false;
      if (
        this.admin ||
        this.billingSettingType == "daily" ||
        (this.employee && this.employee.enableEditBillingSetting)
      ) {
        res = true;
      }
      return res;
    },
    enableSendApproveBillingSetting() {
      let res = false;
      if (
        this.admin ||
        this.billingSettingType == "daily" ||
        (this.employee && this.employee.enableSendApproveBillingSetting)
      ) {
        res = true;
      }
      return res;
    },
    billingTable() {
      return {
        headers: [
          {
            value: "order",
            text: this.$t('rank'),
            width: "5%"
          },
          {
            value: "name",
            text: this.$t('area.area_name'),
            width: "10%",
            filter: d =>
              this.filterData.areaName
                ? d
                  .toLowerCase()
                  .includes(this.filterData.areaName.toLowerCase())
                : d
          },
          {
            value: "serviceText",
            text: this.$t('service'),
            align: "center",
            sortable: false,
            filter: d =>
              this.filterData.serviceName
                ? d
                  .toLowerCase()
                  .includes(this.filterData.serviceName.toLowerCase())
                : d
          },
          {
            value: "unitText",
            text: this.$t('area.method_calculate'),
            align: "center",
            sortable: false,
            width: "12%",
            filter: d =>
              this.filterData.unit
                ? d.toLowerCase().includes(this.filterData.unit.toLowerCase())
                : d
          },
          {
            value: "costText",
            text: `${this.$t('price')}`,
            align: "right",
            sortable: false,
            width: "12%",
            filter: d =>
              this.filterData.price
                ? d
                  .toLowerCase()
                  .includes(this.filterData.price.toLowerCase())
                : d
          },
          {
            value: "discountText",
            text: `${this.$t('discount')}`,
            align: "right",
            sortable: false,
            width: "12%",
            filter: d =>
              this.filterData.discount
                ? d
                  .toLowerCase()
                  .includes(this.filterData.discount.toLowerCase())
                : d
          },
          {
            value: "totalText",
            text: `${this.$t('total')}`,
            align: "right",
            sortable: false,
            width: "12%",
            filter: d =>
              this.filterData.total
                ? d
                  .toLowerCase()
                  .includes(this.filterData.total.toLowerCase())
                : d
          },
          {
            value: "action",
            text: "",
            align: "center",
            sortable: false,
            width: "8%"
          },
          {
            value: "add",
            text: "",
            align: "right",
            sortable: false,
            width: "8%"
          }
        ]
      };
    }
  },
  watch: {
    areaBillingSettingItems: {
      handler() {
        this.filterOption.unit = [];
        this.areaBillingSettingItems.forEach(val => {
          val.units.forEach(unit => {
            if (!this.filterOption.unit.includes(unit)) {
              this.filterOption.unit.push(unit);
            }
          });
        })
        this.filterOption.unit.push("-");
      },
    },
  },
  data() {
    return {
      removeAreaId: "",
      billingDialog: false,
      billingType: "",
      event: "",
      tempBillingSetting: {},
      removeId: "",
      removeDialog: false,
      tempMeter: {},
      isBillingLoading: false,
      tempArea: null,
      isSaveing: false,
      isShowSelect: false,
      selectType: "",
      seleteAreas: [],
      seleteMeters: [],
      isLoadingDialog: false,
      loadingInitial: 0,
      loadingFinal: 0,
      isLoading: false,
      formBill: null,
      isGotoSendSettingTask: false,
      addCostBillingDialog: false,
      addCostBillingSettings: {},
      filterData: {
        areaName: null,
        meterName: null,
        serviceName: null,
        unit: null,
        price: null,
        discount: null,
        total: null
      },
      filterOption: {
        unit: [],
      },
      billButtonType: "monthly",
      dialogCloseDialogConfirm: false,
    };
  },
  mounted() {
    this.initData();
  },
  methods: {
    initData() {
      this.$emit("onInitBilling");
    },
    removeDialogId(id, bill) {
      this.removeId = id;
      this.removeDialog = true;
      this.removeAreaId = bill.area.objectId;
    },
    async removeBilling() {
      try {
        this.isLoading = true;
        await this.createSettingTask(
          {
            ...{
              action: "remove",
              billingSettingId: this.removeId,
              marketId: this.marketId,
              areaId: this.removeAreaId
            }
          },
          "remove"
        );
        this.removeDialog = false;
        this.isLoading = false;
        this.removeAreaId = "";
        this.initData();
      } catch (err) {
        this.isLoading = false;
        this.removeDialog = false;
        this.removeAreaId = "";
      }
    },
    selectUpdate(billingSetting, type, area, meter) {
      this.billingType = type;
      this.event = "update";
      this.tempBillingSetting = billingSetting;
      this.tempArea = area;
      this.tempMeter = meter;
      this.isSaveList = false;
      this.billingDialog = true;
    },
    onSaveBilling(formData) {
      if (this.isSaveList) {
        this.saveBillingList(formData);
      } else {
        this.saveBilling(formData);
      }
    },
    async saveBilling(formData) {
      try {
        this.isBillingLoading = true;
        await this.createSettingTask(
          {
            ...formData,
            ...{ action: "add", marketId: this.marketId }
          },
          "add"
        );
        this.billingDialog = false;
        this.isBillingLoading = false;
        this.initData();
      } catch (err) {
        console.log("error", err);
        this.isBillingLoading = false;
      }
    },
    async saveBillingList(formData) {
      try {
        this.isBillingLoading = true;
        this.isLoadingDialog = true;
        this.loadingInitial = 0;
        this.loadingFinal = 0;
        let postData = formData;
        this.loadingInitial = this.seleteAreas.length;
        for (let area of this.seleteAreas) {
          postData.areaId = area.objectId;
          let billingSetting = area.billingSettings.find(
            d =>
              d.accountingProduct.objectId == formData.accountingProductId &&
              d.type == formData.type
          );
          if (billingSetting) {
            postData.id = billingSetting.objectId;
            postData.billingSettingId = billingSetting.objectId;
            await this.createSettingTask(
              {
                ...postData,
                ...{ action: "edit", marketId: this.marketId }
              },
              "edit",
              false
            );
          } else if (postData.cost > 0 || postData.discount > 0) {
            await this.createSettingTask(
              {
                ...postData,
                ...{ action: "add", marketId: this.marketId }
              },
              "add",
              false
            );
          }
          this.loadingFinal++;
        }
        if (!this.enableEditBillingSetting) {
          this.isGotoSendSettingTask = true;
        }
        this.isShowSelect = false;
        this.seleteAreas = [];
        this.seleteMeters = [];

        this.billingDialog = false;
        this.isLoadingDialog = false;
        this.isBillingLoading = false;
        this.initData();
      } catch (err) {
        this.isBillingLoading = false;
      }
    },
    async saveAddCostList(formData) {
      try {
        this.isBillingLoading = true;
        this.isLoadingDialog = true;
        this.loadingInitial = 0;
        this.loadingFinal = 0;
        let postData = formData;

        this.loadingInitial = this.seleteAreas.length;
        for (let area of this.seleteAreas) {
          postData.areaId = area.objectId;
          let billingSetting = area.billingSettings.find(
            d =>
              d.accountingProduct.objectId ==
              formData.selectProduct.accountingProductId
          );
          if (billingSetting) {
            postData.id = billingSetting.objectId;
            postData.billingSettingId = billingSetting.objectId;

            let cost = 0;
            if (postData.addType == "cost")
              cost = billingSetting.cost + parseFloat(formData.cost);
            else if (postData.addType == "percent")
              cost =
                billingSetting.cost +
                (billingSetting.cost * parseFloat(formData.percent)) / 100;

            cost = this.$helper.round(cost, !postData.isRound);

            if (cost < 0) cost = 0;
            await this.createSettingTask(
              {
                id: billingSetting.objectId,
                billingSettingId: billingSetting.objectId,
                accountingProductId: formData.selectProduct.accountingProductId,
                areaId: area.objectId,
                action: "edit",
                marketId: this.marketId,
                discount: billingSetting.discount,
                cost
              },
              "edit",
              false
            );
          }
          this.loadingFinal++;
        }
        if (!this.enableEditBillingSetting) {
          this.isGotoSendSettingTask = true;
        }
        this.isShowSelect = false;
        this.seleteAreas = [];
        this.seleteMeters = [];

        this.billingDialog = false;
        this.addCostBillingDialog = false;
        this.isLoadingDialog = false;
        this.isBillingLoading = false;
        this.initData();
      } catch (err) {
        this.isBillingLoading = false;
      }
    },
    async saveUpdateBilling(value) {
      try {
        this.isBillingLoading = true;
        await this.createSettingTask(
          {
            ...value,
            ...{ action: "edit", marketId: this.marketId }
          },
          "edit"
        );
        this.billingDialog = false;
        this.isBillingLoading = false;
        this.initData();
      } catch (err) {
        this.isBillingLoading = false;
      }
    },
    addBillingSetting(area, type, meterObj = {}) {
      this.billingType = type;
      this.event = "create";
      this.tempMeter = meterObj;
      this.tempArea = area;
      this.tempBillingSetting = null;
      this.isSaveList = false;
      this.billingDialog = true;
    },
    seleteArea(type = "setting") {
      this.selectType = type;
      this.seleteAreas = [];
      this.isShowSelect = true;
    },
    addBillingSettings() {
      this.tempArea = null;
      (this.tempMeter = null), (this.tempBillingSetting = null);
      this.event = "create";
      this.billingType = "service";
      this.isSaveList = true;
      this.billingDialog = true;
    },
    removeBillingSettings() {
      this.tempArea = null;
      (this.tempMeter = null), (this.tempBillingSetting = null);
      this.event = "delete";
      this.billingType = "service";
      this.isSaveList = true;
      this.billingDialog = true;
    },
    async saveDeleteBilling(formData) {
      try {
        this.isBillingLoading = true;
        this.isLoadingDialog = true;
        this.loadingInitial = 0;
        this.loadingFinal = 0;
        this.loadingInitial = this.seleteAreas.length;
        for (let area of this.seleteAreas) {
          let billingSetting = area.billingSettings.find(
            d =>
              d.accountingProduct.objectId == formData.accountingProductId &&
              d.type == formData.type
          );
          if (billingSetting) {
            await this.createSettingTask(
              {
                ...{
                  action: "remove",
                  billingSettingId: billingSetting.objectId,
                  marketId: this.marketId,
                  areaId: billingSetting.area.objectId
                }
              },
              "remove"
            );
          }
          this.loadingFinal++;
        }

        this.isShowSelect = false;
        this.seleteAreas = [];
        this.seleteMeters = [];

        this.billingDialog = false;
        this.isLoadingDialog = false;
        this.isBillingLoading = false;
        this.initData();
      } catch (err) {
        console.log("err", err);
        this.isBillingLoading = false;
      }
    },
    addCosts() {
      this.addCostBillingSettings = {};
      for (const seleteArea of this.seleteAreas) {
        for (let billingSetting of seleteArea.billingSettings) {
          billingSetting.area.name = seleteArea.name;
          if (!this.addCostBillingSettings[billingSetting.objectId])
            this.addCostBillingSettings[
              billingSetting.objectId
            ] = billingSetting;
        }
      }
      this.addCostBillingDialog = true;
    },
    async createSettingTask(formData, action, isSingle = true) {
      if (!this.enableEditBillingSetting) {
        if (formData && formData.cost == 0 && formData.discount == 0)
          formData.action = "remove";
        await this.$store.dispatch("settingTask/createSettingTask", {
          ...formData,
          settingTaskType: "billing_setting",
          currencyCode: this.tabType
        });

        if (isSingle) this.isGotoSendSettingTask = true;
        this.$toast.show({
          content: this.$t('market.setting_price_bill_send', { action: action == "add" ? this.$t('action_type.add') : action == "edit" ? this.$t('action_type.edit') : "-" }),
          color: "success"
        });
      } else {
        if (action == "add") {
          if (this.billingSettingType == "daily") {
            await this.$store.dispatch(
              "dailyBillingSetting/createDailyBillingSetting",
              {
                ...formData,
                currencyCode: this.tabType
              }
            );
          } else if (this.billingSettingType == "monthly") {
            await this.$store.dispatch(
              "billingSetting/createBillingSetting",
              {
                ...formData,
                currencyCode: this.tabType
              }
            );
          }
        } else if (action == "edit") {
          if (this.billingSettingType == "daily") {
            await this.$store.dispatch(
              "dailyBillingSetting/updateDailyBillingSetting",
              {
                ...formData,
                currencyCode: this.tabType
              }
            );
          } else if (this.billingSettingType == "monthly") {
            await this.$store.dispatch(
              "billingSetting/updateBillingSetting",
              {
                ...formData,
                currencyCode: this.tabType
              }
            );
          }
        } else if (action == "remove") {
          if (this.billingSettingType == "daily") {
            await this.$store.dispatch(
              "dailyBillingSetting/removeDailyBillingSetting",
              {
                id: formData.billingSettingId
              }
            );
          } else if (this.billingSettingType == "monthly") {
            await this.$store.dispatch("billingSetting/removeBillingSetting", {
              id: formData.billingSettingId
            });
          }
        }
        this.$toast.show({
          content: this.$t('market.finish_setting_invoice', { action: action == "add" ? this.$t('action_type.add') : action == "edit" ? this.$t('action_type.edit') : "-" }),
          color: "success"
        });
      }
      if (isSingle) this.initData();
    },
    changeBillingSettingType(value) {
      this.$emit("changeBillingSettingType", value);
      this.clearFilter()
    },
    clearFilter() {
      this.filterData = {
        areaName: null,
        meterName: null,
        serviceName: null,
        unit: null,
        price: null,
        discount: null,
        total: null
      }
    }
  }
};
</script>

<style>
.nameProd {
  font-size: 1rem;
}

.v-data-table::v-deep th {
  color: #000;
  font-size: 1rem !important;
  text-align: center !important;
}

.v-data-table::v-deep td {
  font-size: 1rem !important;
  text-align: center !important;
  padding-right: 2rem !important;
}
</style>